import * as React from 'react'

import {
  CreateAgreementFormPacketModel,
  CreateAgreementTemplateDetails,
  CreateAgreementTemplateDetailsError,
} from '@user/components/Agreements/AgreementFormPacketDialog/typings'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import IconButton from '@mui/material/IconButton'
import { useAgreementPacketCardActions } from '@user/components/Agreements/AgreementFormPacketDialog/hooks/useAgreementPacketCardActions'
import { ENTITY_TYPES } from '@sales-center/common'
import { useFormikContext } from 'formik'
import { DraggableFormPacketErrors } from '@user/components/Agreements/AgreementFormPacketDialog/DraggableFormPacketErrors'
import Typography from '@mui/material/Typography'
import { TemplatePreviewPopover } from '@user/components/Agreements/CreateAgreementDrawer/CreateAgreementForm/TemplateDetails/Preview/PreviewPopover'
import Box from '@mui/material/Box'
import { DraggableAgreementServicesListWrapper } from './DraggableAgreementServicesListWrapper'

type Props = {
  agreement: CreateAgreementTemplateDetails
  index: number
  entityId: number
  entityType: ENTITY_TYPES
  openServicesDrawer: () => void
}

export function DraggableAgreementCard(props: Props) {
  const { agreement, index, entityId, entityType, openServicesDrawer } = props
  const { errors } = useFormikContext<CreateAgreementFormPacketModel>()

  const { handleTemplateDelete, getServiceLocationName } =
    useAgreementPacketCardActions(entityType)
  const templateErrors =
    errors.templates as CreateAgreementTemplateDetailsError[]

  const formError = templateErrors?.find((error) => error.index === index)

  return (
    <Box sx={{ display: 'flex', cursor: 'grab', pb: 1.5 }}>
      <DragIndicatorIcon
        fontSize="medium"
        data-test-id="Drag-indicator-icon"
        sx={{ m: '4px' }}
      />
      <Card
        sx={{
          width: '100%',
          border: `1px solid #D3D7DC`,
          overflow: 'visible',
        }}
        variant="outlined"
      >
        <Stack
          sx={{
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F3F5F7',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
          }}
        >
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <Typography variant="subtitle3" mr={1.5} ml={1} lineHeight="23px">
              {agreement.templateFormName}
            </Typography>

            <TemplatePreviewPopover
              templateId={agreement.templateId}
              color="default"
            />
          </Stack>

          <IconButton
            onClick={() => handleTemplateDelete(index)}
            sx={{ padding: '4px', mr: '14px', marginY: '4px' }}
            data-test-id="agreement-card-delete-button"
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>

        <DraggableFormPacketErrors
          agreement={agreement}
          formError={formError}
          openServicesDrawer={openServicesDrawer}
          entityId={entityId}
          entityType={entityType}
        />

        <DraggableAgreementServicesListWrapper
          type={agreement.templateId.toString()}
          services={agreement.services || []}
          getServiceLocationName={getServiceLocationName}
        />
      </Card>
    </Box>
  )
}
